import { Controller } from "@hotwired/stimulus"
import { v4 as uuidv4 } from "uuid"
import Sortable from "sortablejs"

// Connects to data-controller="footer-editor"
export default class extends Controller {
  static targets = ["columnsContainer"]

  connect() {
    console.log("Footer Editor connected")
    this.loadFooterData()
    this.initSortable()
    this.initInputListeners()
  }

  initSortable() {
    // Make columns sortable
    new Sortable(this.columnsContainerTarget, {
      animation: 150,
      handle: '.handle',
      ghostClass: 'brightness-60',
      onEnd: () => {
        this.updateColumnOrders()
      }
    })

    // Make items within columns sortable
    document.querySelectorAll('.items-container').forEach(container => {
      new Sortable(container, {
        animation: 150,
        handle: '.item-handle',
        ghostClass: 'brightness-60',
        onEnd: () => {
          this.updateItemOrders(container.closest('.column-container').dataset.columnId)
        }
      })
    })
  }

  loadFooterData() {
    // Fetch footer data from the server
    fetch('/admin/footer_sections/data')
      .then(response => response.json())
      .then(data => {
        if (data && data.length > 0) {
          // Clear existing columns
          this.columnsContainerTarget.innerHTML = ''

          // Render each column
          data.sort((a, b) => a.order - b.order).forEach(column => {
            this.renderColumn(column)
          })

          // Initialize sortable for newly added columns
          this.initSortable()

          // Update preview
          this.updateFooterPreview()
        }
      })
      .catch(error => {
        console.error('Error loading footer data:', error)
      })
  }

  renderColumn(column) {
    // Clone the column template
    const template = document.getElementById('column-template')
    const columnElement = template.content.cloneNode(true).querySelector('.column-container')

    // Set column ID and title
    columnElement.dataset.columnId = column.id
    columnElement.querySelector('.column-title').value = column.title || ''

    // Render items
    const itemsContainer = columnElement.querySelector('.items-container')
    if (column.items && column.items.length > 0) {
      column.items.sort((a, b) => a.order - b.order).forEach(item => {
        const itemElement = this.createItemElement(item)
        itemsContainer.appendChild(itemElement)
      })
    }

    // Add column to the container
    this.columnsContainerTarget.appendChild(columnElement)
  }

  createItemElement(item) {
    let template, itemElement

    if (item.type === 'link') {
      template = document.getElementById('link-item-template')
      itemElement = template.content.cloneNode(true).querySelector('.item-container')
      itemElement.dataset.itemId = item.id

      // Set link values
      itemElement.querySelector('.item-link').value = item.link || ''
      itemElement.querySelector('.item-link-text').value = item.link_text || ''

      // Set icon if present
      const iconInput = itemElement.querySelector('.item-icon')
      const iconPreview = itemElement.querySelector('.icon-preview')
      if (item.icon) {
        iconInput.value = item.icon
        iconPreview.innerHTML = `<iconify-icon icon="${item.icon}"></iconify-icon>`
      }
    } else {
      template = document.getElementById('paragraph-item-template')
      itemElement = template.content.cloneNode(true).querySelector('.item-container')
      itemElement.dataset.itemId = item.id

      // Set content for textarea
      const contentTextarea = itemElement.querySelector('.item-content')
      contentTextarea.value = item.content || ''
    }

    return itemElement
  }

  addColumn() {
    const columnId = `col-${uuidv4()}`
    const column = {
      id: columnId,
      title: 'New Column',
      items: [],
      order: this.getNextColumnOrder()
    }

    this.renderColumn(column)
    this.initSortable()
    this.updateFooterPreview()
  }

  removeColumn(event) {
    const columnElement = event.target.closest('.column-container')
    columnElement.remove()
    this.updateFooterPreview()
  }

  addLinkItem(event) {
    const columnElement = event.target.closest('.column-container')
    const columnId = columnElement.dataset.columnId
    const itemsContainer = columnElement.querySelector('.items-container')

    const itemId = `item-${columnId}-${uuidv4()}`
    const item = {
      id: itemId,
      type: 'link',
      content: null,
      link: '',
      link_text: '',
      icon: null,
      order: this.getNextItemOrder(columnId)
    }

    const itemElement = this.createItemElement(item)
    itemsContainer.appendChild(itemElement)
    this.updateFooterPreview()
  }

  addParagraphItem(event) {
    const columnElement = event.target.closest('.column-container')
    const columnId = columnElement.dataset.columnId
    const itemsContainer = columnElement.querySelector('.items-container')

    const itemId = `item-${columnId}-${uuidv4()}`
    const item = {
      id: itemId,
      type: 'paragraph',
      content: '',
      link: null,
      link_text: null,
      icon: null,
      order: this.getNextItemOrder(columnId)
    }

    const itemElement = this.createItemElement(item)
    itemsContainer.appendChild(itemElement)
    this.updateFooterPreview()
  }

  removeItem(event) {
    const itemElement = event.target.closest('.item-container')
    itemElement.remove()
    this.updateFooterPreview()
  }

  // Handle icon input change to update preview
  updateIconPreview(event) {
    const iconInput = event.target
    const itemElement = iconInput.closest('.item-container')
    const iconPreview = itemElement.querySelector('.icon-preview')

    if (iconInput.value) {
      iconPreview.innerHTML = `<iconify-icon icon="${iconInput.value}"></iconify-icon>`
    } else {
      iconPreview.innerHTML = ''
    }

    // Update the footer preview
    this.updateFooterPreview()
  }

  updateColumnOrders() {
    const columns = this.columnsContainerTarget.querySelectorAll('.column-container')
    columns.forEach((column, index) => {
      column.dataset.order = index + 1
    })
    this.updateFooterPreview()
  }

  updateItemOrders(columnId) {
    const columnElement = this.columnsContainerTarget.querySelector(`.column-container[data-column-id="${columnId}"]`)
    if (columnElement) {
      const items = columnElement.querySelectorAll('.item-container')
      items.forEach((item, index) => {
        item.dataset.order = index + 1
      })
    }
    this.updateFooterPreview()
  }

  getNextColumnOrder() {
    const columns = this.columnsContainerTarget.querySelectorAll('.column-container')
    return columns.length + 1
  }

  getNextItemOrder(columnId) {
    const columnElement = this.columnsContainerTarget.querySelector(`.column-container[data-column-id="${columnId}"]`)
    if (columnElement) {
      const items = columnElement.querySelectorAll('.item-container')
      return items.length + 1
    }
    return 1
  }

  // Initialize input listeners for live preview updates
  initInputListeners() {
    // Use event delegation to listen for input changes on the entire container
    this.element.addEventListener('input', (event) => {
      const target = event.target

      // Check if the input is one we want to monitor for changes
      if (target.classList.contains('column-title') ||
          target.classList.contains('item-link') ||
          target.classList.contains('item-link-text') ||
          target.classList.contains('item-content')) {

        // Don't update on icon input as that's handled separately
        if (!target.classList.contains('item-icon')) {
          this.updateFooterPreview()
        }
      }
    })
  }

  // Move a column up in the order
  moveColumnUp(event) {
    const columnElement = event.target.closest('.column-container')
    const previousColumn = columnElement.previousElementSibling
    
    if (previousColumn) {
      this.columnsContainerTarget.insertBefore(columnElement, previousColumn)
      this.updateColumnOrders()
    }
  }

  // Move a column down in the order
  moveColumnDown(event) {
    const columnElement = event.target.closest('.column-container')
    const nextColumn = columnElement.nextElementSibling
    
    if (nextColumn) {
      this.columnsContainerTarget.insertBefore(nextColumn, columnElement)
      this.updateColumnOrders()
    }
  }

  // Move an item up in the order
  moveItemUp(event) {
    const itemElement = event.target.closest('.item-container')
    const previousItem = itemElement.previousElementSibling
    const itemsContainer = itemElement.parentElement
    const columnId = itemElement.closest('.column-container').dataset.columnId
    
    if (previousItem) {
      itemsContainer.insertBefore(itemElement, previousItem)
      this.updateItemOrders(columnId)
    }
  }

  // Move an item down in the order
  moveItemDown(event) {
    const itemElement = event.target.closest('.item-container')
    const nextItem = itemElement.nextElementSibling
    const itemsContainer = itemElement.parentElement
    const columnId = itemElement.closest('.column-container').dataset.columnId
    
    if (nextItem) {
      itemsContainer.insertBefore(nextItem, itemElement)
      this.updateItemOrders(columnId)
    }
  }

  updateFooterPreview() {
    const footerData = this.collectFooterData()
    const previewContainer = document.getElementById('footer-preview')

    // Clear existing preview
    previewContainer.innerHTML = ''

    // Generate preview HTML
    footerData.sort((a, b) => a.order - b.order).forEach(column => {
      const columnDiv = document.createElement('div')
      columnDiv.className = 'flex-1'

      if (column.title) {
        const titleDiv = document.createElement('div')
        titleDiv.className = 'font-bold text-xl text-white mb-6'
        titleDiv.textContent = column.title
        columnDiv.appendChild(titleDiv)
      }

      if (column.items && column.items.length > 0) {
        const itemsDiv = document.createElement('div')
        itemsDiv.className = 'text-slate-500'

        const itemsList = document.createElement('ul')

        column.items.sort((a, b) => a.order - b.order).forEach(item => {
          const listItem = document.createElement('li')
          listItem.className = 'mb-2 flex items-start'

          if (item.type === 'link') {
            const link = document.createElement('a')
            link.href = item.link || '#'
            link.className = 'text-slate-400 hover:text-slate-200 transition flex items-center gap-2'

            if (item.icon) {
              const iconEl = document.createElement('iconify-icon')
              iconEl.setAttribute('icon', item.icon)
              link.appendChild(iconEl)
            }

            link.appendChild(document.createTextNode(item.link_text || (item.link ? item.link.split('/').pop() : 'Link')))
            listItem.appendChild(link)
          } else {
            const contentDiv = document.createElement('div')
            contentDiv.className = 'prose prose-invert max-w-none'
            contentDiv.innerHTML = item.content || 'Paragraph'
            listItem.appendChild(contentDiv)
          }

          itemsList.appendChild(listItem)
        })

        itemsDiv.appendChild(itemsList)
        columnDiv.appendChild(itemsDiv)
      }

      previewContainer.appendChild(columnDiv)
    })
  }

  collectFooterData() {
    const footerData = []

    const columns = this.columnsContainerTarget.querySelectorAll('.column-container')
    columns.forEach((columnElement, columnIndex) => {
      const columnId = columnElement.dataset.columnId
      const columnTitle = columnElement.querySelector('.column-title').value

      const column = {
        id: columnId,
        title: columnTitle,
        items: [],
        order: columnElement.dataset.order || (columnIndex + 1)
      }

      const items = columnElement.querySelectorAll('.item-container')
      items.forEach((itemElement, itemIndex) => {
        const itemId = itemElement.dataset.itemId
        const itemType = itemElement.dataset.itemType

        let item = {
          id: itemId,
          type: itemType,
          order: itemElement.dataset.order || (itemIndex + 1)
        }

        if (itemType === 'link') {
          item.content = null
          item.link = itemElement.querySelector('.item-link').value
          item.link_text = itemElement.querySelector('.item-link-text').value
          item.icon = itemElement.querySelector('.item-icon').value || null
        } else {
          const contentInput = itemElement.querySelector('.item-content')
          item.content = contentInput.value
          item.link = null
          item.link_text = null
          item.icon = null
        }

        column.items.push(item)
      })

      footerData.push(column)
    })

    return footerData
  }

  saveFooter() {
    const footerData = this.collectFooterData()

    // Send data to server
    fetch('/admin/footer_sections', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ footer_data: footerData })
    })
    .then(response => {
      if (response.ok) {
        alert('Footer saved successfully!')
        return response.json()
      }
      throw new Error('Failed to save footer data')
    })
    .catch(error => {
      console.error('Error saving footer data:', error)
      alert('Failed to save footer data. Please try again.')
    })
  }
}
